/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import React, { ReactElement } from 'react';
import { Stacklight, UaPlaceholder } from 'templates/pubSub/types/TemplateType';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { usePubSubNode, usePubSubNodes, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import StacklightComponent from './StacklightComponent';
import { FilterStartNode } from 'utilities/PubSub/NodeFilters';

interface IStacklightComponentPlaceholderProps {
  templateContext: TemplateContext;
  stacklight: Stacklight;
  placeholder: UaPlaceholder;
  placeholderIndex: number;
  startingNodeId?: NodeIdentifier;
}

export default function StacklightComponentPlaceholder(props: IStacklightComponentPlaceholderProps): ReactElement {
  const filter = TemplateHelperPubSub.createFilter(props.templateContext.namespaces, props.placeholder.variablePath);
  const node = usePubSubNode(filter, props.startingNodeId);
  const placeholderFilter = TemplateHelperPubSub.createReferenceAndTypeFilter(
    props.templateContext.namespaces,
    props.placeholder.reference,
    props.placeholder.type,
  );
  const placeholderNodes = usePubSubNodes(placeholderFilter, node);

  const sortingFilter = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.stacklight.sortingVariable?.variablePath,
  );
  const sortingFilterObjects = placeholderNodes.map((nId) => ({
    [nId]: [FilterStartNode(nId)].concat(sortingFilter),
  }));
  const sortingFilters = Object.assign({}, ...sortingFilterObjects);
  const sortingNodes = usePubSubNode(sortingFilters);
  const sortingValues = usePubSubValue(sortingNodes);

  const placeholderNodesSorted =
    !sortingValues || sortingValues.length !== placeholderNodes.length
      ? placeholderNodes
      : placeholderNodes.sort((a, b) => sortingValues[b] - sortingValues[a]);

  const nextPlaceholder =
    props.stacklight.lampPlaceholders && props.stacklight.lampPlaceholders.length > props.placeholderIndex + 1
      ? props.stacklight.lampPlaceholders[props.placeholderIndex + 1]
      : undefined;

  return (
    <>
      {placeholderNodesSorted.map((placeholderNode, placeholderNodeIndex) => (
        <React.Fragment key={TemplateHelperPubSub.getPlaceholderObjectKey('stacklight', placeholderNodeIndex)}>
          {nextPlaceholder ? (
            <StacklightComponentPlaceholder
              templateContext={props.templateContext}
              stacklight={props.stacklight}
              placeholder={nextPlaceholder}
              placeholderIndex={props.placeholderIndex + 1}
              startingNodeId={placeholderNode}
            />
          ) : (
            <StacklightComponent
              templateContext={props.templateContext}
              stacklight={props.stacklight}
              startingNodeId={placeholderNode}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
}
